import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { Button, Input } from '@/components/ui';
import rgfLogo from '@/assets/rgf-logo.png';

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
  rememberMe: z.boolean().default(false),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error } = useAuth();
  const { showError, showSuccess } = useNotification();

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const result = await login({ 
        email: data.email, 
        password: data.password,
        rememberMe: data.rememberMe 
      });
      
      if (result.meta.requestStatus === 'fulfilled') {
        showSuccess('Login Successful', 'Welcome to RGF Back Office');
        navigate(from, { replace: true });
      } else {
        const errorMessage = result.payload as string || 'Login failed';
        showError('Login Failed', errorMessage);
      }
    } catch (err: any) {
      showError('Login Failed', err.message || 'An unexpected error occurred');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rgf-primary-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img
              className="h-12 w-auto"
              src={rgfLogo}
              alt="Roundglass Foundation"
            />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-rgf-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-sm text-rgf-gray-600">
            Access the Back Office Management Console
          </p>
        </div>

        <div className="card">
          <div className="card-body">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <Input
                {...register('email')}
                type="email"
                label="Email address"
                placeholder="Enter your email"
                startIcon={<Mail className="h-5 w-5" />}
                error={errors.email?.message}
                disabled={isLoading}
                autoComplete="email"
                required
              />

              <div className="relative">
                <Input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  placeholder="Enter your password"
                  startIcon={<Lock className="h-5 w-5" />}
                  error={errors.password?.message}
                  disabled={isLoading}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-9 text-rgf-gray-400 hover:text-rgf-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>

              {/* Remember Me temporarily hidden as it's not working properly */}
              {/* <div className="flex items-center">
                <input
                  {...register('rememberMe')}
                  type="checkbox"
                  className="h-4 w-4 text-rgf-primary-600 focus:ring-rgf-primary-500 border-rgf-gray-300 rounded"
                  disabled={isLoading}
                />
                <label className="ml-2 block text-sm text-rgf-gray-700">
                  Remember me
                </label>
              </div> */}

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">
                    {error}
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                size="lg"
                loading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-rgf-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-rgf-gray-500">
                    Need help accessing your account?
                  </span>
                </div>
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-rgf-gray-600">
                  Contact your administrator for account access
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-rgf-gray-500">
          <p>
            &copy; {new Date().getFullYear()} Roundglass Foundation. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};